import React, { useState } from 'react'
import { Fab, Tooltip } from '@mui/material'
import { alpha } from '@mui/material/styles'
import ContactSupportIcon from '@mui/icons-material/ContactSupport'
import { useLocation } from 'react-router-dom'

import { useAuth } from '../../contexts/AuthContext'
import { useAppInfo } from '../../hooks/useVersion'
import { poolwerxColors } from '../../theme/colors'
import { BOTTOM_CHROME_RESERVE_PX, BOTTOM_CHROME_Z_INDEX, FAB_HEIGHT_PX } from './bottomChrome'
import { useFeedbackUI } from './FeedbackUIContext'
import { readLastUsedTab } from './lastUsedTab'

// Public / auth-redirect paths where the FAB must not render. The
// `useAuth().user == null` check below covers anonymous flows
// (login, OAuth bounce); the explicit list prevents a flash during
// in-flight auth transitions.
const HIDE_PREFIXES = ['/login', '/oauth/', '/invitation/']

const HelpFeedbackFab: React.FC = () => {
  const { user } = useAuth()
  const location = useLocation()
  const { environment } = useAppInfo()
  const { open, openDrawer } = useFeedbackUI()
  const [hovered, setHovered] = useState(false)

  if (!user) return null
  if (HIDE_PREFIXES.some(p => location.pathname.startsWith(p))) return null
  if (open) return null

  // DevEnvironmentBanner is `position: sticky; bottom: 0` and ~40px tall;
  // while the user is scrolling it sits at the viewport bottom, so lift
  // the FAB above it when running in dev.
  const isDev = environment === 'development'
  const bottomOffset = isDev ? BOTTOM_CHROME_RESERVE_PX + 24 : 24

  const handleClick = () => {
    openDrawer({ tab: readLastUsedTab() ?? 'submit' })
  }

  // Quiet resting pill ("Help") that expands to the full label on hover or
  // keyboard focus — `hovered` is driven by both so focus gets the same
  // expanded state as hover.
  const expanded = hovered

  return (
    <Tooltip title="Help & feedback" placement="left" disableHoverListener={expanded}>
      <Fab
        variant="extended"
        aria-label="Open help and feedback"
        onClick={handleClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        sx={{
          position: 'fixed',
          bottom: bottomOffset,
          right: 24,
          width: 'auto',
          height: FAB_HEIGHT_PX,
          minHeight: FAB_HEIGHT_PX,
          px: 2,
          // Quiet teal at rest so it doesn't compete with page content;
          // full teal on hover/focus.
          bgcolor: hovered ? poolwerxColors.teal : alpha(poolwerxColors.teal, 0.88),
          color: '#fff',
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.875rem',
          letterSpacing: 0.2,
          gap: 0.75,
          transition:
            'width 180ms ease, background-color 180ms ease, box-shadow 180ms ease',
          boxShadow: hovered ? 4 : 1,
          zIndex: BOTTOM_CHROME_Z_INDEX + 1,
          '&:hover': { bgcolor: poolwerxColors.teal },
        }}
      >
        <ContactSupportIcon sx={{ fontSize: 22 }} />
        <span style={{ whiteSpace: 'nowrap' }}>
          {expanded ? 'Help & feedback' : 'Help'}
        </span>
      </Fab>
    </Tooltip>
  )
}

export default HelpFeedbackFab
